import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import React from "react";
import { SurveyBody } from "../../components/SurveyBody";
import { PageHeader } from "../../components/PageHeader";
import { Edit, ExpandMore } from "@mui/icons-material";
import { isEmptyObject } from "../../util";
import { startCase, get, isArray, unset } from "lodash";
import { SurveyFormData } from "../SurveyContainer";

function ReviewHeader() {
  return (
    <PageHeader
      title="Review your answers"
      body={[
        "Here’s your chance to review your questionnaire answers. Click the pencil icon to make edits.",
        "After you’ve reviewed your answers, click on the results button at the end of this page, and you’ll be taken to the list of funding programs that match your answers.",
      ]}
    />
  );
}

interface ReviewAccordionProps {
  title: string;
  subtitle: string;
  reviewPercent: number;
  children: React.ReactNode;
}

function ReviewAccordion({
  title,
  subtitle,
  reviewPercent,
  children,
}: ReviewAccordionProps) {
  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ flexDirection: "column" }}>
            <Typography sx={{ fontWeight: "600" }}>{title}</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <LinearProgress
                sx={{ width: "15rem" }}
                variant="determinate"
                value={reviewPercent}
              />
              <Typography sx={{ marginLeft: 1, fontSize: ".9rem" }}>
                {reviewPercent}%
              </Typography>
            </Box>
            <Typography sx={{ fontSize: ".9rem" }}>{subtitle}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>{children}</AccordionDetails>
      </Accordion>
    </>
  );
}

interface ReviewItemProps {
  name: string;
  editItem: () => void;
  children: React.ReactNode;
}

function ReviewItem({ name, editItem, children }: ReviewItemProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        padding: 2,
      }}
    >
      <Box sx={{ flexDirection: "column" }}>
        <Typography sx={{ fontWeight: "600" }}>{name}</Typography>
        {children}
      </Box>
      <IconButton onClick={editItem}>
        <Edit />
      </IconButton>
    </Box>
  );
}

interface ReviewRowProps {
  name: string;
  value: object | string | number | [];
  editItem: (name: string) => void;
}

function ReviewRow({ name, value, editItem }: ReviewRowProps) {
  const formattedName = get({}, name, startCase(name));

  // For some array based fields, we want to render them as newline-based lists instead of
  // comma separated strings for readability purposes
  const fieldsToRenderAsLists = ["schoolAndMajorChoices", "affiliations"];

  if (isArray(value)) {
    const cleanedValues = value.join(", ");
    return (
      <ReviewItem name={formattedName} editItem={() => editItem(name)}>
        {fieldsToRenderAsLists.includes(name) && value.length > 0 ? (
          value.map((val, idx) => (
            <Typography key={`${val}-${idx}`}>{val}</Typography>
          ))
        ) : (
          <Typography>{cleanedValues || "No answer."}</Typography>
        )}
      </ReviewItem>
    );
  }
  if (typeof value === "object") {
    // Given an object type, this reduce outputs a string of values within the object
    // If the object's values are booleans, the key will be output instead of a value when the value is True
    const cleanedValues = Object.entries(value).reduce(
      (acc, [currKey, currVal], currentIndex) => {
        if (currVal) {
          if (typeof currVal === "boolean") {
            acc += `${currKey}`;
          } else {
            acc += `${currVal}`;
          }
          // add trailing comma if this is not the last iteration
          if (currentIndex !== Object.values(value).length - 1) {
            acc += `, `;
          }
        }
        return acc;
      },
      ""
    );
    return (
      <ReviewItem name={formattedName} editItem={() => editItem(name)}>
        <Typography>{cleanedValues || "No answer."}</Typography>
      </ReviewItem>
    );
  }
  return (
    <ReviewItem name={formattedName} editItem={() => editItem(name)}>
      <Typography>{value || "No answer."}</Typography>
    </ReviewItem>
  );
}

type TReviewPageData = {
  [key: string]: object | string | number | [];
};

interface ReviewPageProps {
  data: TReviewPageData;
  headerText: string;
  editItem: (name: string) => void;
}

function ReviewPage({ data, headerText, editItem }: ReviewPageProps) {
  const totalQuestions = Object.keys(data).length;
  const answeredQuestions = Object.values(data).reduce((acc: number, curr) => {
    // We need to check for non-empty objects a bit differently than numbers/strings/arrays
    if (typeof curr === "object") {
      if (!isEmptyObject(curr)) {
        acc += 1;
      }
    } else {
      if (curr) {
        acc += 1;
      }
    }
    return acc;
  }, 0);

  const subtitle = `${answeredQuestions} out of ${totalQuestions} questions complete.`;
  const reviewPercent = Math.round((answeredQuestions / totalQuestions) * 100);

  return (
    <ReviewAccordion
      title={headerText}
      subtitle={subtitle}
      reviewPercent={reviewPercent}
    >
      {Object.entries(data).map(([name, value], idx) => {
        return (
          <ReviewRow
            editItem={editItem}
            name={name}
            value={value}
            key={`${name}-${idx}`}
          />
        );
      })}
    </ReviewAccordion>
  );
}

interface ReviewProps {
  setCurrentPage: (page: string) => void;
}
export function Review({ setCurrentPage }: ReviewProps) {
  const { getValues } = useFormContext<SurveyFormData>();
  const formValues = getValues();

  // Page 1
  const requiredFieldValues = {
    age: formValues.demographics.age,
    currentGrade: formValues.academicInfo.currentSchool.gradeLevel,
    "Current GPA": formValues.academicInfo.currentSchool.gpa,
    "Desired Degree": formValues.collegeInfo.scholarshipPurpose,
    "Enrollment Status": formValues.collegeInfo.enrollmentStatus,
  };

  // Page 2
  const testScores = formValues.academicInfo.testScores
    .filter((score) => score.test !== "" && score.score !== "")
    .map((score) => {
      return `${score.test}: ${score.score}`;
    });
  const academicInfoValues = {
    ...formValues.academicInfo,
    testScores: testScores.length > 0 ? testScores.join(", ") : "",
    graduationDate: formValues.academicInfo.graduationStatus?.graduationDate
      ? new Date(
          formValues.academicInfo.graduationStatus.graduationDate
        ).toLocaleDateString()
      : "",
    currentLocation: formValues.demographics.currentLocation,
    permanentLocation: formValues.demographics.permanentLocation,
  };
  unset(academicInfoValues, "graduationStatus");
  unset(academicInfoValues, "permanentLocation.same_as_current");

  // Page 3
  const collegeInfoValues = {
    "College Readiness Programs":
      formValues.collegeInfo.collegeReadinessPrograms,
    // We don't want to display all the auxiliary information included in the NSPA School and Major types, we just want
    // school and major names
    schoolAndMajorChoices: formValues.collegeInfo.schoolAndMajorChoices
      .filter(
        (samChoice: { schoolName?: string; majorName?: string }) =>
          samChoice.schoolName || samChoice.majorName
      )
      .map((samChoice: { schoolName?: string; majorName?: string }) => {
        return `${samChoice.schoolName || "No school selected"} - ${
          samChoice.majorName || "No major selected"
        }`;
      }),
  };

  // Page 4
  const additionalInfoValues = {
    ...formValues.additionalInfo,
    // Display affilition relation
    affiliations: formValues.additionalInfo.affiliations
      .filter(
        (affiliationChoice: {
          entityName?: string;
          directRelation?: string;
          indirectRelation?: string;
        }) =>
          affiliationChoice.entityName ||
          affiliationChoice.directRelation ||
          affiliationChoice.indirectRelation
      )
      .map(
        (affiliationChoice: {
          entityName?: string;
          directRelation?: string;
          indirectRelation?: string;
        }) => {
          return `${affiliationChoice.entityName} - direct relation: ${
            affiliationChoice.directRelation
          } - relation to the affiliated person: ${
            affiliationChoice.indirectRelation || "not set"
          }`;
        }
      ),
  };
  // Condition is displayed in demographic values now
  unset(additionalInfoValues, "condition");

  // Demographic data, page 5
  const demographicsValues = {
    gender: formValues.sensitive.gender,
    "Self Identification": formValues.sensitive.sexuality,
    "Race and Ethnicity": formValues.sensitive.race,
    hispanic: formValues.sensitive.ethnicity,
    ancestry: formValues.sensitive.ancestry,
    "Citizenship Status": formValues.sensitive.citizenshipStatuses,
    Conditions: formValues.sensitive.condition,
  };

  return (
    <>
      <ReviewHeader />
      <SurveyBody>
        <Box
          sx={{
            paddingY: 2,
            rowGap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ReviewPage
            data={requiredFieldValues}
            headerText={"Required Fields"}
            editItem={() => setCurrentPage("1")}
          />
          <ReviewPage
            data={academicInfoValues}
            headerText={"Current Status"}
            editItem={() => setCurrentPage("2")}
          />
          <ReviewPage
            data={collegeInfoValues}
            headerText={"College Information"}
            editItem={() => setCurrentPage("3")}
          />
          <ReviewPage
            data={additionalInfoValues}
            headerText={"Additional Info"}
            editItem={(name) => {
              // Condition is sensitive information and we're displaying it on page 5,
              // but it lives under the Additional Information review panel
              if (name === "condition") {
                return setCurrentPage("5");
              }
              setCurrentPage("4");
            }}
          />
          <ReviewPage
            data={demographicsValues}
            headerText={"Demographics"}
            editItem={() => setCurrentPage("5")}
          />
        </Box>
      </SurveyBody>
    </>
  );
}
